import { v4 as uuid4 } from 'uuid';
import { buildSignature, AesCBCEncryptV2 } from './encryption';
import CryptoJS from 'crypto-js';

import { encryptV3, getNativeInfo, getHeaderV3, isInApp, EUFY_SECURITY_APPLICATION_NAME, isPR, getAppName, getAppHeaderName } from '@docs/common'
import { AesCBCEncrypt, buildUUidString, CustomHeaders, getEcdhKeyLocalStorage } from '../../utils/secure';
import { exchangeKey } from '../secureRequest';
import { getCookie } from '../../utils/cookie';
import { _getGToken, _getToken } from '../clientReq';

interface RandomField {
  unisign: string;
  keyindent: string;
  once: string;
  ts: number;
}

interface CredentialHeaders {
  randomField: RandomField;
  headers: Record<string, string>;
}

interface EcdhKey {
  shareKey: string;
  keyIndent: string;
}

const buildRandomString = (): string => uuid4().replace(/-/g, '');

const buildRandomField = (): RandomField => ({
  unisign: buildRandomString(),
  keyindent: buildRandomString(),
  once: buildRandomString(),
  ts: Math.round(Date.now() / 1000),
});

const buildBasicHeaders = (): CredentialHeaders => {
  const randomField = buildRandomField();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Replay-Info': 'replay',
    'X-Request-Ts': randomField.ts.toString(),
    'X-Request-Once': randomField.once,
  };
  return { randomField, headers };
};

export const buildCredentialHeaders = (): CredentialHeaders => {
  const { randomField, headers } = buildBasicHeaders();
  return { randomField, headers };
};

export const buildExchangeKeyHeaders = (key: string, body: string): CredentialHeaders => {
  const { randomField, headers } = buildCredentialHeaders();
  const signature = buildSignature(key, randomField.ts, randomField.once, body);
  headers['X-Signature'] = signature;
  headers['X-Key-Ident'] = randomField.keyindent;
  headers['App-Name'] = "eufy_security";
  return { randomField, headers };
};

const encryptBody = async (body: string, key: string): Promise<string> => {
  if (!body || !key) return body;
  return await AesCBCEncryptV2(body, key);
};

export const getEdchKeySession = async (): Promise<any> => {
  try {
    const nativeInfo = await getNativeInfo();
    const domain = nativeInfo?.domain;
    const key = btoa(`edch_key_${domain}`);
    return key ? getSession(key) : '';
  } catch (e) {
    return '';
  }
};

export const getSession = (key: string): unknown => {
  let data: unknown;
  const rawData = sessionStorage.getItem(key);
  if (rawData) {
    const decData = CryptoJS.enc.Base64.parse(rawData).toString(CryptoJS.enc.Utf8);
    const decJson = CryptoJS.AES.decrypt(decData, 'aes').toString(CryptoJS.enc.Utf8);
    data = JSON.parse(decJson);
  }
  return data;
};


// 在h5环境下，构建v3接口的请求头
export const buildV3HeadersH5 = async (
  body: string,
  ecdhKey: { shareKey: string; keyIndent: string }
): Promise<{ randomField: RandomField; headers: CustomHeaders; encBody: string }> => {
  const { randomField, headers } = buildBasicHeaders()
  const encBody = body !== '' ? await AesCBCEncrypt(body, ecdhKey.shareKey) : ''
  const signature = await buildSignature(ecdhKey.shareKey, randomField.ts, randomField.once, encBody)
  const appName = getCookie('app_name') || EUFY_SECURITY_APPLICATION_NAME
  headers['X-Encryption-Info'] = 'algo_ecdh'
  headers['Model-Type'] = 'PHONE'
  headers['X-Signature'] = signature
  headers['X-Key-Ident'] = ecdhKey.keyIndent
  headers['App-Name'] = appName
  return { randomField, headers, encBody }
}

export const buildV3HeadersApp = async (body: string, domain?: string): Promise<Record<string, string>> => {
  const ts = String(Math.round(Date.now() / 1000))
  const once = buildUUidString()
  const headers = await getHeaderV3(ts, once, body, domain)
  const gtoken =  await _getGToken()
  const appName = await getAppHeaderName()

  // 不走代理
  return {
    'App-Name': appName,
    'Content-Type': 'text/plain',
    'X-Encryption-Info': 'algo_ecdh',
    'X-Replay-Info': 'replay',
    'X-Request-Ts': ts,
    'X-Request-Once': once,
    'Model-Type': 'PHONE',
    Gtoken: gtoken,
    ...headers,
  }
};

// 在并发请求的情况下，在模块级别添加一个变量来存储exchangeKey的Promise
let exchangeKeyPromise: any = null

// 缓存ecdhKey，避免重复请求exchangeKey
let ecdhKeyCache: { shareKey: string; keyIndent: string } | null = null

// 获取ecdhKey，如果缓存中没有则发起exchangeKey请求
const getEcdhKey = async () => {
  if (!ecdhKeyCache) {
    ecdhKeyCache = await getEcdhKeyLocalStorage()
    if (!ecdhKeyCache) {
      if (!exchangeKeyPromise) {
        // 如果exchangeKeyPromise不存在，则创建一个新的Promise
        exchangeKeyPromise = exchangeKey()
      }
      // 等待exchangeKeyPromise解决，无论是新创建的还是已经存在的
      ecdhKeyCache = await exchangeKeyPromise
      // 清除exchangeKeyPromise，以便下次需要时可以重新创建
      exchangeKeyPromise = null
    }
  }
  return ecdhKeyCache
}

export const requestV3Headers = async (
  config: {
    url?: string
    data?: any
    [key: string]: any
  },
  domain?: string
): Promise<{ v3_headers: Record<string, string | number>; config: any }> => {
  let v3_headers: Record<string, string | number> = {}
  const isFormData = config.data instanceof FormData
  console.log('🚀 ~ file: clientReq.ts ~ line 157 ~ isMegaUrl ~ isFormData', isFormData)

  // v3 如果不是在app内
  if (!(await isInApp())) {
    const ecdhKey = await getEcdhKey()
    const data = config.data ? JSON.stringify(config.data) : ''
    console.log('🚀 ~ requestV3Headers ~ data', data)
    const { headers, encBody } = await buildV3HeadersH5(data, ecdhKey!)
    if (isFormData) {
      // Content-Type 保持是multipart/form-data
      v3_headers = {
        ...headers,
        'Content-Type': 'multipart/form-data',
      }
    } else {
      v3_headers = {
        ...headers,
        'Content-Type': 'text/plain',
      }
    }
    // 网关不会去解密，对表单接口，但是又需要把加密参数传过去，只是不进行body的加密
    if (data && !isFormData) {
      config['data'] = encBody
    }
  } else {
    // v3 如果是在app内
    console.log('🚀 ~ requestV3Headers ~ data', config.data)
    const newData = config.data && !isFormData ? await encryptV3({ type: 0, data: JSON.stringify(config.data), domain }) : ''
    const newHeader = await buildV3HeadersApp(newData, domain)
    console.log('🚀 ~ file: header.ts ~ line 116 ~ requestV3Headers', newHeader)
    if (isFormData) {
      // 这里需要删除app传过来的 Content-Type，因为值是text/plain
      delete newHeader['Content-Type']
      v3_headers = {
        'X-Auth-Token': await _getToken(),
        ...newHeader,
      }
    } else {
      v3_headers = { 'X-Auth-Token': await _getToken(), ...newHeader }
    }
    // 网关不会去解密，对表单接口，但是又需要把加密参数传过去，只是不进行body的加密
    if (newData && !isFormData) {
      config['data'] = newData
    }
    config = {
      ...config,
      credentials: 'omit',
      body: isFormData ? config?.data : newData,
    }
  }

  return { v3_headers, config }
}